import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Verified, Building2, Search, MapPinned, X } from "lucide-react";
import AppLayout from "../components/AppLayout";
import ClubFormDialog from "../components/ClubFormDialog";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { COUNTRY_LIST } from "../lib/countries";

const TYPE_FILTERS = [
  { key: "all",        label: "All"    },
  { key: "clubs",      label: "Clubs"  },
  { key: "facilities", label: "Venues" },
];

/**
 * Unified Clubs directory — clubs + verified facilities live in the same
 * module. Filter chips for type, sport, country and a city search line up
 * with the FacilitiesDirectory UX so users find what they need fast.
 */
export default function Clubs() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [sports, setSports] = useState([]);
  const [type, setType] = useState("all");
  const [sport, setSport] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [q, setQ] = useState("");
  const [topCities, setTopCities] = useState([]);

  useEffect(() => {
    api.get("/sports").then((r) => setSports(r.data || [])).catch(() => setSports([]));
    api.get("/clubs").then((r) => setClubs(r.data || [])).catch(() => setClubs([]));
    api.get("/facilities?verified=true&limit=120")
      .then((r) => setFacilities(r.data?.results || []))
      .catch(() => setFacilities([]));
    // Top-12 cities for the discovery ribbon — each links to /clubs/in/{slug}
    // which emits a CollectionPage JSON-LD and concentrates link equity.
    api.get("/discovery/cities?limit=12")
      .then((r) => setTopCities(r.data?.cities || []))
      .catch(() => setTopCities([]));
  }, []);

  // Build a slug→id lookup so the sport filter can match clubs (which store
  // sport_ids) and facilities (which store sport_slugs) with one selection
  const sportSlugToId = useMemo(() => {
    const m = {};
    for (const s of sports) m[s.slug] = s.id;
    return m;
  }, [sports]);

  const merged = useMemo(() => {
    const clubItems = clubs.map((c) => ({
      kind: "club",
      id: c.id,
      to: `/clubs/${c.slug || c.id}`,
      name: c.name,
      description: c.description || "",
      city: c.city, country: c.country,
      verified: !!c.is_verified,
      logo_url: c.logo_url,
      sport_slugs: (c.sport_configs || []).map((cfg) => cfg.sport_slug),
      sport_ids: c.sport_ids || [],
      primary_facility: c.primary_facility || null,
    }));
    const facItems = facilities.map((f) => ({
      kind: "facility",
      id: f.id,
      to: `/facilities/${f.slug || f.id}`,
      name: f.name,
      description: [f.address, f.city, f.country].filter(Boolean).join(" · "),
      city: f.city, country: f.country,
      verified: !!f.verified,
      logo_url: null,
      sport_slugs: f.sport_slugs || [],
      sport_ids: [],
    }));
    let all = [...clubItems, ...facItems];
    if (type === "clubs") all = clubItems;
    else if (type === "facilities") all = facItems;
    if (sport) {
      const wantedId = sportSlugToId[sport];
      all = all.filter((it) =>
        it.sport_slugs.includes(sport) ||
        (wantedId && it.sport_ids.includes(wantedId)),
      );
    }
    if (country) all = all.filter((it) => (it.country || "").toUpperCase() === country.toUpperCase());
    if (city.trim()) {
      const cn = city.trim().toLowerCase();
      all = all.filter((it) => (it.city || "").toLowerCase().includes(cn));
    }
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      all = all.filter((it) =>
        (it.name || "").toLowerCase().includes(needle) ||
        (it.city || "").toLowerCase().includes(needle) ||
        (it.country || "").toLowerCase().includes(needle),
      );
    }
    all.sort((a, b) => Number(b.verified) - Number(a.verified) || a.name.localeCompare(b.name));
    return all;
  }, [clubs, facilities, type, sport, country, city, q, sportSlugToId]);

  const hasActiveFilter = sport || country || city || q;
  const clearAll = () => { setSport(""); setCountry(""); setCity(""); setQ(""); };

  return (
    <AppLayout>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black">{t("clubs")}</h1>
          <p className="text-zinc-400 mt-2">Discover clubs and venues, join, play.</p>
        </div>
        {user && <ClubFormDialog mode="create" onSaved={(c) => navigate(`/clubs/${c.id}`)} />}
      </div>

      {/* Type filter pills */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="clubs-type-row">
        {TYPE_FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`clubs-filter-${f.key}`}
            onClick={() => setType(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
              type === f.key
                ? "bg-[#00E676] text-black border-[#00E676]"
                : "border-white/10 text-zinc-400 hover:border-white/30"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Sport pills — All + each sport */}
      {sports.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="clubs-sport-row">
          <button
            data-testid="clubs-sport-all"
            onClick={() => setSport("")}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
              !sport ? "bg-white text-black border-white" : "border-white/10 text-zinc-400 hover:border-white/30"
            }`}
          >
            All sports
          </button>
          {sports.map((s) => (
            <button
              key={s.slug}
              data-testid={`clubs-sport-${s.slug}`}
              onClick={() => setSport(s.slug)}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                sport === s.slug ? "bg-white text-black border-white" : "border-white/10 text-zinc-400 hover:border-white/30"
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      {/* Popular cities — each link hits /clubs/in/{city_slug} which has its
          own JSON-LD CollectionPage and concentrates link equity for SEO. */}
      {topCities.length > 0 && (
        <div className="mt-4" data-testid="clubs-top-cities">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Popular cities</div>
          <div className="flex flex-wrap gap-2">
            {topCities.map((c) => (
              <Link
                key={`${c.city_slug}-${c.country}`}
                to={`/clubs/in/${c.city_slug}`}
                data-testid={`clubs-top-city-${c.city_slug}`}
                className="px-3 py-1.5 rounded-full text-xs border border-[#00E676]/20 text-[#00E676] hover:bg-[#00E676]/10 transition-colors flex items-center gap-1"
              >
                <MapPinned size={11} />
                {c.city}
                <span className="text-[10px] text-[#00E676]/60">· {c.facility_count + c.club_count}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Country + City + Search row */}
      <div className="mt-3 flex flex-wrap gap-2 items-stretch" data-testid="clubs-loc-row">
        <select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          data-testid="clubs-country"
          className="pp-input text-sm max-w-xs"
        >
          <option value="">All countries</option>
          {COUNTRY_LIST.map((c) => <option key={c.iso} value={c.iso}>{c.name}</option>)}
        </select>
        <input
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="City"
          data-testid="clubs-city"
          className="pp-input text-sm max-w-[180px]"
        />
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-2.5 text-zinc-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, city, or country…"
            data-testid="clubs-search"
            className="pp-input ps-9 w-full text-sm"
          />
        </div>
        {hasActiveFilter && (
          <button
            onClick={clearAll}
            data-testid="clubs-clear-filters"
            className="pp-chip text-xs flex items-center gap-1"
          >
            <X size={11} /> Clear filters
          </button>
        )}
      </div>

      <div className="mt-3 text-xs text-zinc-500" data-testid="clubs-count">
        {merged.length} {merged.length === 1 ? "result" : "results"}
        {type !== "all" && ` · ${type}`}
        {sport && ` · ${sport}`}
        {country && ` · ${country}`}
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="clubs-grid">
        {merged.map((it) => <DirectoryCard key={`${it.kind}-${it.id}`} it={it} />)}
        {merged.length === 0 && (
          <div className="pp-card p-10 text-zinc-500 col-span-full text-center" data-testid="clubs-empty">
            {t("no_results")}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function DirectoryCard({ it }) {
  const isClub = it.kind === "club";
  return (
    <Link
      to={it.to}
      data-testid={`${isClub ? "club" : "facility"}-card-${it.id}`}
      className="pp-card p-5 block hover:border-[#00E676]/40 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`shrink-0 h-9 w-9 rounded-lg flex items-center justify-center ${isClub ? "bg-[#00E676]/10" : "bg-[#007AFF]/10"}`}>
            {isClub ? <Building2 size={16} className="text-[#00E676]" /> : <MapPinned size={16} className="text-[#007AFF]" />}
          </div>
          <div className="min-w-0">
            <div className="font-heading text-lg font-bold truncate flex items-center gap-1.5">
              <span className="truncate">{it.name}</span>
              {it.verified && <Verified size={14} className="text-[#00E676] shrink-0" />}
            </div>
            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
              isClub ? "bg-[#00E676]/10 text-[#00E676]" : "bg-[#007AFF]/10 text-[#007AFF]"
            }`}>
              {isClub ? "Club" : "Venue"}
            </span>
          </div>
        </div>
      </div>
      {it.description && <div className="text-sm text-zinc-400 mt-3 line-clamp-2">{it.description}</div>}
      <div className="mt-3 text-xs text-zinc-500 flex items-center gap-1 truncate">
        <MapPin size={12} /> {[it.city, it.country].filter(Boolean).join(", ") || "—"}
      </div>
      {it.sport_slugs?.length > 0 && (
        <div className="mt-2 flex gap-1 flex-wrap">
          {it.sport_slugs.slice(0, 4).map((s) => (
            <span key={s} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-400 uppercase">{s}</span>
          ))}
        </div>
      )}
      {isClub && it.primary_facility && (
        <div
          className="mt-2 text-[11px] text-[#007AFF] flex items-center gap-1 truncate"
          data-testid={`club-card-${it.id}-primary-facility`}
        >
          <MapPinned size={11} />
          <span className="truncate">Plays at {it.primary_facility.name}</span>
        </div>
      )}
    </Link>
  );
}
